// === PERSONENAUTO — calculation payload builder + BFF-result merge ===
// Pure functions extracted from PersonenAutoScreen. buildAutoPayload constructs the BFF
// /productservice/calculate request for a Hoofddekking variant; mergeAutoResult folds a BFF
// response back into the workflow data. The impure inputs (now / baseUrl / appName) are injected
// so both stay pure and unit-testable offline (see calc.test.ts). Mirrors rechtsbijstand/calc.ts.

import { bffClient } from '@sluitstraat/core';
import { applyCaDerivedFields, getActiveGarantie } from '../../../utils/autoGarantie';
import { orderAutoCoverages } from '../../../utils/autoSectionOrder';
import { sortAfdDocument } from '../ongevallen/calc';
import {
  AUTO_PRODUCT,
  AUTO_PP_DEFAULTS,
  AUTO_WA_INSURED_SUMS,
  AUTO_CODELIST,
  AUTO_FIELD_ALLOWLIST,
  afmkmCode,
  afmkmWireKm,
} from './fields';
import { hasCascoSelected, type AutoVariant } from './coverages';

export interface BuildAutoPayloadOptions {
  /** Injected clock — replaces new Date() so PP_INGDAT/HVVDAT/PH-age are deterministic in tests. */
  now: Date;
  /** Application base URL (window.location.origin + '/') for AL_URLNAAM. */
  baseUrl: string;
  /** Resolved application name for AL_APPLNM (await getApplicationName() in the screen). */
  appName: string;
  /** Workflow relation data — clientInfo seeds BS when the driver is the policyholder. */
  relationData: any;
  /** Config-driven SV_VERZSOM default (passenger-damage insured sum). */
  passengerInsuredSumDefault: number;
}

export function buildAutoPayload(baseData: any, hoofddekking: AutoVariant, opts: BuildAutoPayloadOptions) {
  const { now, baseUrl, appName, relationData, passengerInsuredSumDefault } = opts;

    const enrichedData = JSON.parse(JSON.stringify(baseData));

    // Ensure Contractdocument.PP exists
    if (!enrichedData.Contractdocument) enrichedData.Contractdocument = {};
    if (!enrichedData.Contractdocument.PP) enrichedData.Contractdocument.PP = {};

    // Strip BA from request (only used locally for address lookup)
    delete enrichedData.Contractdocument.PP.BA;

    // === PP-level defaults ===
    if (!enrichedData.Contractdocument.PP.PP_BETTERM) enrichedData.Contractdocument.PP.PP_BETTERM = 1;

    {
      const today = now;
      const dateN8 = Number(`${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`);
      if (!enrichedData.Contractdocument.PP.PP_INGDAT) enrichedData.Contractdocument.PP.PP_INGDAT = dateN8;
      if (!enrichedData.Contractdocument.PP.PP_HVVDAT) {
        // Hoofdvervaldatum = first day of the current month, next year
        const hvvMonth = String(today.getMonth() + 1).padStart(2, '0');
        enrichedData.Contractdocument.PP.PP_HVVDAT = Number(`${today.getFullYear() + 1}${hvvMonth}01`);
      }
      enrichedData.Contractdocument.PP.PP_TARDAT = dateN8;
    }

    if (!enrichedData.Contractdocument.PP.PP_MYAAND) enrichedData.Contractdocument.PP.PP_MYAAND = AUTO_PRODUCT.ppMyaand;
    if (!enrichedData.Contractdocument.PP.PP_PRODNM) enrichedData.Contractdocument.PP.PP_PRODNM = AUTO_PRODUCT.productName;
    if (!enrichedData.Contractdocument.PP.PP_PRODUCT) enrichedData.Contractdocument.PP.PP_PRODUCT = enrichedData.Contractdocument.PP.PP_PRODNM;
    if (!enrichedData.Contractdocument.PP.PP_CDUUMND) enrichedData.Contractdocument.PP.PP_CDUUMND = AUTO_PP_DEFAULTS.PP_CDUUMND;
    if (!enrichedData.Contractdocument.PP.PP_AFDDEFN) enrichedData.Contractdocument.PP.PP_AFDDEFN = AUTO_PRODUCT.afdDefn;
    if (!enrichedData.Contractdocument.PP.PP_BRANCHE) enrichedData.Contractdocument.PP.PP_BRANCHE = AUTO_PRODUCT.branchCode;
    if (!enrichedData.Contractdocument.PP.PP_INCWIJZ) enrichedData.Contractdocument.PP.PP_INCWIJZ = AUTO_PRODUCT.incWijze;
    // 14041: PP_HANDACC - manual acceptance J/N
    if (!enrichedData.Contractdocument.PP.PP_HANDACC) enrichedData.Contractdocument.PP.PP_HANDACC = AUTO_PP_DEFAULTS.PP_HANDACC;
    if (!enrichedData.Contractdocument.PP.PP_STPRV) enrichedData.Contractdocument.PP.PP_STPRV = AUTO_PP_DEFAULTS.PP_STPRV;
    // PP_EKST - Servicevergoeding (config-driven via prolongationFee)
    enrichedData.Contractdocument.PP.PP_EKST = AUTO_PP_DEFAULTS.PP_EKST;
    if (enrichedData.Contractdocument.PP.PP_IBAN === undefined) enrichedData.Contractdocument.PP.PP_IBAN = '';
    if (!enrichedData.Contractdocument.PP.PP_SPLIMNR) enrichedData.Contractdocument.PP.PP_SPLIMNR = '';
    // 10046: PP_BMNAAND - B/M trede = schadevrije jaren + inschalingstrede
    const schdvry = Number(enrichedData.Contractdocument.PP.PP_SCHDVRY) || 0;
    const bminsch = Number(enrichedData.Contractdocument.PP.PP_BMINSCH) || AUTO_CODELIST.PP_BMINSCH;
    enrichedData.Contractdocument.PP.PP_BMNAAND = schdvry + bminsch;
    delete enrichedData.Contractdocument.PP.PP_SCHDVRM;
    // 10048: PP_BMINSCH - B/M inschalingstrede
    if (enrichedData.Contractdocument.PP.PP_BMINSCH === undefined) enrichedData.Contractdocument.PP.PP_BMINSCH = AUTO_CODELIST.PP_BMINSCH;
    // PP_BMEXTRA - B/M extra opslag
    if (enrichedData.Contractdocument.PP.PP_BMEXTRA === undefined) enrichedData.Contractdocument.PP.PP_BMEXTRA = AUTO_PP_DEFAULTS.PP_BMEXTRA;
    // 10208: PP_IVBLETC - IVB lettercode
    if (!enrichedData.Contractdocument.PP.PP_IVBLETC) enrichedData.Contractdocument.PP.PP_IVBLETC = AUTO_CODELIST.PP_IVBLETC;
    // 12363: PP_SPECLIM - special limit J/N
    if (!enrichedData.Contractdocument.PP.PP_SPECLIM) enrichedData.Contractdocument.PP.PP_SPECLIM = AUTO_PP_DEFAULTS.PP_SPECLIM;
    // PP_PPRC - provisiepercentage
    if (enrichedData.Contractdocument.PP.PP_PPRC === undefined) enrichedData.Contractdocument.PP.PP_PPRC = AUTO_PP_DEFAULTS.PP_PPRC;

    // === VE defaults ===
    if (!enrichedData.Contractdocument.PP.VE) enrichedData.Contractdocument.PP.VE = {};
    if (!enrichedData.Contractdocument.PP.VE.VE_MYAAND) enrichedData.Contractdocument.PP.VE.VE_MYAAND = AUTO_PRODUCT.veMyaand;

    // === BS defaults (Bestuurder/driver) ===
    if (!enrichedData.Contractdocument.PP.BS) enrichedData.Contractdocument.PP.BS = {};
    const isDriverSameAsPolicyholder = (baseData?.Supportsection?.Divers?.Hoofdbestuurder ?? 'J') === 'J';
    const clientInfo = relationData?.clientInfo;

    if (isDriverSameAsPolicyholder) {
      // Hoofdbestuurder = J: driver IS the policyholder, fill BS from client info
      enrichedData.Contractdocument.PP.BS.BS_RELSRTC = 'A';
      if (clientInfo) {
        if (clientInfo.lastName) enrichedData.Contractdocument.PP.BS.BS_ANAAM = clientInfo.lastName;
        if (clientInfo.initials) enrichedData.Contractdocument.PP.BS.BS_VOORL = clientInfo.initials;
        enrichedData.Contractdocument.PP.BS.BS_GESLACH = clientInfo.gender || 'O';
      }
      if (relationData?.clientInfo?.dateOfBirth) {
        let dateStr = relationData.clientInfo.dateOfBirth.replace(/-/g, '');
        if (dateStr.length === 8) {
          const day = dateStr.substring(0, 2);
          const month = dateStr.substring(2, 4);
          const year = dateStr.substring(4, 8);
          dateStr = year + month + day;
        }
        const dateNum = Number(dateStr);
        if (!isNaN(dateNum) && dateStr.length === 8) {
          enrichedData.Contractdocument.PP.BS.BS_GEBDAT = dateNum;
        }
      }
    } else {
      // Hoofdbestuurder = N: driver is different, use user-entered BS data (only set defaults if missing)
      if (!enrichedData.Contractdocument.PP.BS.BS_RELSRTC) enrichedData.Contractdocument.PP.BS.BS_RELSRTC = 'A';
    }
    // Always populate BS address from VP (partner/child assumed same address as policyholder)
    const vp = enrichedData.Contractdocument.PP.VP || {};
    if (vp.VP_HUISNR) enrichedData.Contractdocument.PP.BS.BS_HUISNR = vp.VP_HUISNR;
    if (vp.VP_PCODE) enrichedData.Contractdocument.PP.BS.BS_PCODE = vp.VP_PCODE;
    if (vp.VP_PLAATS) enrichedData.Contractdocument.PP.BS.BS_PLAATS = vp.VP_PLAATS;
    if (vp.VP_STRAAT) enrichedData.Contractdocument.PP.BS.BS_STRAAT = vp.VP_STRAAT;
    if (vp.VP_TOEVOEG) enrichedData.Contractdocument.PP.BS.BS_TOEVOEG = vp.VP_TOEVOEG;

    if (enrichedData.Contractdocument.PP.VP && !enrichedData.Contractdocument.PP.VP.VP_GEBDAT && enrichedData.Contractdocument.PP.BS.BS_GEBDAT) {
      enrichedData.Contractdocument.PP.VP.VP_GEBDAT = enrichedData.Contractdocument.PP.BS.BS_GEBDAT;
    }

    // Ensure WA is always present with required fields
    if (!enrichedData.Contractdocument.PP.WA) {
      enrichedData.Contractdocument.PP.WA = { WA_CODE: '2001', WA_PPRC: 20 };
    }
    if (enrichedData.Contractdocument.PP.WA.WA_SCHDVRY === undefined) {
      enrichedData.Contractdocument.PP.WA.WA_SCHDVRY = enrichedData.Contractdocument.PP.PP_SCHDVRY || 0;
    }

    // === Set CA based on Hoofddekking variant ===
    if (hoofddekking === 'WA') {
      enrichedData.Contractdocument.PP.CA = [];
    } else if (hoofddekking === 'WA+') {
      enrichedData.Contractdocument.PP.CA = [{
        CA_CODE: '2003',
        CA_ENTREF: '2003',
        CA_ERB: 0,
        CA_PPRC: enrichedData.Contractdocument.PP.CA?.[0]?.CA_PPRC ?? 20,
      }];
    } else if (hoofddekking === 'WA+volledig') {
      enrichedData.Contractdocument.PP.CA = [{
        CA_CODE: '2002',
        CA_ENTREF: '2002',
        CA_ERB: enrichedData.Contractdocument.PP.CA?.[0]?.CA_ERB ?? 0,
        CA_PPRC: enrichedData.Contractdocument.PP.CA?.[0]?.CA_PPRC ?? 20,
      }];
    }

    // === Always include ALL optional coverages to get premiums upfront ===

    // RP - Rechtsbijstand. Schema requires an object; coerce arrays/non-objects (a stale BFF
    // response or earlier merge can leave RP as `[null]`, which serializes as `"RP":[null]`).
    {
      const rp = enrichedData.Contractdocument.PP.RP;
      if (!rp || typeof rp !== 'object' || Array.isArray(rp)) {
        enrichedData.Contractdocument.PP.RP = {};
      }
    }
    enrichedData.Contractdocument.PP.RP.RP_CODE = '9110';
    if (!enrichedData.Contractdocument.PP.RP.RP_PPRC) enrichedData.Contractdocument.PP.RP.RP_PPRC = 20;

    // SV - Schade voor inzittenden
    if (!enrichedData.Contractdocument.PP.SV) enrichedData.Contractdocument.PP.SV = {};
    enrichedData.Contractdocument.PP.SV.SV_CODE = '1002';
    if (!enrichedData.Contractdocument.PP.SV.SV_PPRC) enrichedData.Contractdocument.PP.SV.SV_PPRC = 20;

    // CG - Coverage extras (only valid with casco). Multiple codes can co-exist (maxItems: 2):
    //   2051 = Vrije keuze herstelbedrijf
    //   2040 = Nieuwwaarde garantie   (Nieuwwaarderegeling toggle — first-owner path)
    //   2041 = Aankoopwaarde garantie (Aanschafwaarderegeling toggle — second-hand path)
    // NW and AKW are mutually exclusive in the UI (OB_EERSTEI gates which toggle the advisor
    // sees), so at most one of {2040, 2041} ends up in the array alongside 2051.
    if (hoofddekking !== 'WA') {
      const cgEntries: any[] = [{ CG_CODE: '2051', CG_PPRC: 20 }];
      // CG 2040 / 2041 only on Volledig Casco — Allianz returns CG_BTP=0 on Beperkt, so
      // pushing the entry there just clutters the request and inflates the response with
      // dead zeros. Mirrors the UI gate (NW/AKW rows hidden outside Volledig). The BFF
      // prices the coverage upfront when the vehicle qualifies, so the per-section premium
      // shows alongside the toggle and only the FINAL strip (handleNext) prunes unselected
      // entries.
      if (hoofddekking === 'WA+volledig') {
        // Push the matching garantie code (2040 NW / 2041 AKW) when the vehicle qualifies
        // so the BFF prices the regeling upfront; the FINAL strip (handleNext) prunes it
        // again when the advisor didn't opt in. Eligibility lives in autoGarantie.ts.
        const active = getActiveGarantie(enrichedData.Contractdocument?.PP?.OB, true);
        if (active) {
          cgEntries.push({ CG_CODE: active.cgCode, CG_PPRC: 20 });
        }
      }
      enrichedData.Contractdocument.PP.CG = cgEntries;
    } else {
      enrichedData.Contractdocument.PP.CG = [];
    }

    // OB defaults (required fields)
    if (enrichedData.Contractdocument.PP.OB) {
      const ob = enrichedData.Contractdocument.PP.OB;
      if (!ob.OB_CARRESS) ob.OB_CARRESS = 'AA';
      if (!ob.OB_GEBRUIK) ob.OB_GEBRUIK = 'P';
      if (!ob.OB_BTWCD) ob.OB_BTWCD = 'I';
      // OB_BOUWMND must be integer per schema (RDW returns string)
      if (ob.OB_BOUWMND !== undefined) {
        ob.OB_BOUWMND = parseInt(String(ob.OB_BOUWMND), 10) || 0;
      }
      // Required OB fields with sensible defaults
      if (!ob.OB_MLDCODE) ob.OB_MLDCODE = '';
      if (!ob.OB_TNOKLAS) ob.OB_TNOKLAS = '';
      if (!ob.OB_TYPE) ob.OB_TYPE = ob.OB_MODEL || '';
      // OB_AANKDAT must be a YYYYMMDD string (same as OB_DATTOEL) — sending it as a number
      // silently breaks Allianz's AKW window check and earns UWM14. Coerce any stale numeric
      // value left over from older state.
      if (typeof ob.OB_AANKDAT === 'number') ob.OB_AANKDAT = String(ob.OB_AANKDAT);
      // AKW active without an entered purchase date: a vehicle first-registered within the
      // window was necessarily purchased within it, so anchor on OB_DATTOEL (mirrors autoGarantie).
      const akwActive = getActiveGarantie(ob, hoofddekking === 'WA+volledig');
      if (akwActive?.cgCode === '2041' && !ob.OB_AANKDAT && ob.OB_DATTOEL) {
        ob.OB_AANKDAT = String(ob.OB_DATTOEL);
      }
      // OB_AFMKM: the engine rates real kilometers, so the wire carries the band km
      // (fields.ts AFMKM table, live-probed 2026-07-17). The dropdown code rides along in
      // EXPERIMENT(OB_AFMKMCD) — self-invented label, safe to remove, see fields.ts.
      if (ob.OB_AFMKM !== undefined) {
        const code = afmkmCode(ob.OB_AFMKM);
        if (code) ob.OB_AFMKMCD = code;
        ob.OB_AFMKM = afmkmWireKm(ob.OB_AFMKM);
      }
      // 10038: OB_AFMWE - weight in kg (from kenteken)
      if (ob.OB_AFMWE !== undefined) ob.OB_AFMWE = parseInt(String(ob.OB_AFMWE), 10) || 0;
      // 10040: OB_VWACA - catalogue value (from kenteken)
      if (ob.OB_VWACA !== undefined) ob.OB_VWACA = parseInt(String(ob.OB_VWACA), 10) || 0;
      // 10177: OB_ZITPLAA - number of seats (from kenteken)
      if (ob.OB_ZITPLAA !== undefined) ob.OB_ZITPLAA = parseInt(String(ob.OB_ZITPLAA), 10) || 0;
      // 10227: OB_AFMCC - cylinder capacity in cc (from kenteken)
      if (ob.OB_AFMCC !== undefined) ob.OB_AFMCC = parseInt(String(ob.OB_AFMCC), 10) || 0;
      // 10236: OB_AUTOTLX - Autotelexcode (from kenteken)
      // 10238: OB_ACCELER - acceleration in seconds (from kenteken)
      if (ob.OB_ACCELER !== undefined) ob.OB_ACCELER = parseFloat(String(ob.OB_ACCELER)) || 0;
      // OB_DAGWRDE - dagwaarde in euros (from kenteken lookup; customer-requested polis data,
      // premium-neutral: live A/B probed 2026-07-15 with identical premiums)
      if (ob.OB_DAGWRDE !== undefined) ob.OB_DAGWRDE = parseInt(String(ob.OB_DAGWRDE), 10) || 0;
      // 10261: OB_FINLEAS - financiering/lease indicator
      if (!ob.OB_FINLEAS) ob.OB_FINLEAS = AUTO_CODELIST.OB_FINLEAS;
      // 11232: OB_OBJSRT - vehicle type code (from kenteken)
      if (!ob.OB_OBJSRT) ob.OB_OBJSRT = 'PE';
      // 11239: OB_VWAAC - accessories value, default 5000
      if (!ob.OB_VWAAC) ob.OB_VWAAC = 5000;
      // 12330: OB_ACTKLR1 - current first colour (from kenteken)
      // 12331: OB_ABS - ABS J/N (from kenteken)
      if (!ob.OB_ABS) ob.OB_ABS = 'N';
      // 13871: OB_DATTOEL - first registration date (from kenteken)
    }

    // === WA defaults for required fields ===
    if (enrichedData.Contractdocument.PP.WA) {
      const wa = enrichedData.Contractdocument.PP.WA;
      // 10047: WA_BMTABCD - B/M table code
      if (!wa.WA_BMTABCD) wa.WA_BMTABCD = AUTO_CODELIST.WA_BMTABCD;
      // 10353: WA_VZBLETS - insured sum bodily injury
      if (wa.WA_VZBLETS === undefined) wa.WA_VZBLETS = AUTO_WA_INSURED_SUMS.WA_VZBLETS;
      // 10354: WA_VZBMATS - insured sum material damage
      if (wa.WA_VZBMATS === undefined) wa.WA_VZBMATS = AUTO_WA_INSURED_SUMS.WA_VZBMATS;
      // WA_WPREMBP — wijze premieberekening (default '4')
      if (!wa.WA_WPREMBP) wa.WA_WPREMBP = '4';
    }

    if (Array.isArray(enrichedData.Contractdocument.PP.CA)) {
      enrichedData.Contractdocument.PP.CA.forEach((ca: any) => {
        // CA_WPREMBP — wijze premieberekening (default '4')
        if (!ca.CA_WPREMBP) ca.CA_WPREMBP = '4';
      });
    }
    applyCaDerivedFields(enrichedData.Contractdocument.PP);

    // === CG defaults (Vrije keuze herstelbedrijf) ===
    if (Array.isArray(enrichedData.Contractdocument.PP.CG)) {
      enrichedData.Contractdocument.PP.CG.forEach((cg: any) => {
        if (!cg.CG_WPREMBP) cg.CG_WPREMBP = '4';
      });
    }

    // === SV defaults ===
    if (enrichedData.Contractdocument.PP.SV) {
      // 11251: SV_VERZSOM - insured sum passenger damage (config-driven default)
      if (enrichedData.Contractdocument.PP.SV.SV_VERZSOM === undefined) {
        enrichedData.Contractdocument.PP.SV.SV_VERZSOM = passengerInsuredSumDefault;
      }
      if (!enrichedData.Contractdocument.PP.SV.SV_WPREMBP) enrichedData.Contractdocument.PP.SV.SV_WPREMBP = '4';
    }

    // === RP defaults (Rechtsbijstand) ===
    if (enrichedData.Contractdocument.PP.RP) {
      if (!enrichedData.Contractdocument.PP.RP.RP_WPREMBP) enrichedData.Contractdocument.PP.RP.RP_WPREMBP = '4';
    }

    // === PH defaults (Pechhulp) ===
    if (enrichedData.Contractdocument.PP.PH) {
      if (!enrichedData.Contractdocument.PP.PH.PH_WPREMBP) enrichedData.Contractdocument.PP.PH.PH_WPREMBP = '4';
    }

    // === EL (2e gezinsauto) defaults ===
    // 12430: EL_BMEXTR2 - B/M extra trede(n) 2e gezinsauto, default 0
    if (!enrichedData.Contractdocument.PP.EL) {
      enrichedData.Contractdocument.PP.EL = { EL_BMEXTR2: 0 };
    }

    // AA (Aanhanger/trailer) is unused by SAA personenauto — AA_MERK only duplicated OB_MERK,
    // and AA is optional at the PP level (not in PP.required). Don't create/send an AA section.
    delete enrichedData.Contractdocument.PP.AA;

    // VP: strip non-schema fields
    if (enrichedData.Contractdocument.PP.VP) {
      const vp = enrichedData.Contractdocument.PP.VP;
      const allowedVP = AUTO_FIELD_ALLOWLIST.VP;
      for (const key of Object.keys(vp)) {
        if (!allowedVP.has(key)) delete vp[key];
      }
    }

    // AL: include application name and URL
    enrichedData.Contractdocument.AL = {
      AL_APPLNM: appName,
      AL_URLNAAM: baseUrl,
      ...(enrichedData.Contractdocument.AL || {}),
    };
    // Ensure BY_BYLSRT is string per schema (BFF may return number)
    if (Array.isArray(enrichedData.Contractdocument.AL.BY)) {
      for (const by of enrichedData.Contractdocument.AL.BY) {
        if (by.BY_BYLSRT !== undefined && typeof by.BY_BYLSRT !== 'string') {
          by.BY_BYLSRT = String(by.BY_BYLSRT);
        }
      }
    }

    // BS: strip non-schema fields (include all schema-required fields)
    if (enrichedData.Contractdocument.PP.BS) {
      const allowedBS = AUTO_FIELD_ALLOWLIST.BS;
      for (const key of Object.keys(enrichedData.Contractdocument.PP.BS)) {
        if (!allowedBS.has(key)) delete enrichedData.Contractdocument.PP.BS[key];
      }
    }

    // OB: strip non-schema fields. OB_EERSTEI (huidige eigenaar is eerste eigenaar) is required
    // by Allianz to validate the Nieuwwaarderegeling (CG 2040) first-owner gate; without it
    // the BFF can't accept 2040. OB_AANKDAT (purchase date) is left in the allowlist for
    // completeness even though we no longer collect it — the AKW window is anchored from
    // OB_DATTOEL / CG_AKPGMND instead.
    if (enrichedData.Contractdocument.PP.OB) {
      const allowedOB = AUTO_FIELD_ALLOWLIST.OB;
      for (const key of Object.keys(enrichedData.Contractdocument.PP.OB)) {
        if (!allowedOB.has(key)) delete enrichedData.Contractdocument.PP.OB[key];
      }
    }

    // VE: only VE_MYAAND allowed
    if (!enrichedData.Contractdocument.PP.VE) enrichedData.Contractdocument.PP.VE = { VE_MYAAND: 'IP01' };
    if (enrichedData.Contractdocument.PP.VE) {
      const allowedVE = AUTO_FIELD_ALLOWLIST.VE;
      for (const key of Object.keys(enrichedData.Contractdocument.PP.VE)) {
        if (!allowedVE.has(key)) delete enrichedData.Contractdocument.PP.VE[key];
      }
    }

    // WA: strip non-schema fields and ensure MP array
    if (enrichedData.Contractdocument.PP.WA) {
      if (!enrichedData.Contractdocument.PP.WA.MP) enrichedData.Contractdocument.PP.WA.MP = [];
      const allowedWA = AUTO_FIELD_ALLOWLIST.WA;
      for (const key of Object.keys(enrichedData.Contractdocument.PP.WA)) {
        if (!allowedWA.has(key)) delete enrichedData.Contractdocument.PP.WA[key];
      }
    }

    // VW: strip non-schema fields and ensure at least 1 item (schema minItems: 1)
    if (Array.isArray(enrichedData.Contractdocument.PP.VW) && enrichedData.Contractdocument.PP.VW.length > 0) {
      const allowedVW = AUTO_FIELD_ALLOWLIST.VW;
      enrichedData.Contractdocument.PP.VW = enrichedData.Contractdocument.PP.VW.map((item: any) => {
        const cleaned: any = {};
        for (const key of Object.keys(item)) {
          if (allowedVW.has(key)) cleaned[key] = item[key];
        }
        return cleaned;
      });
    } else {
      enrichedData.Contractdocument.PP.VW = [{ VW_VOLGNUM: 1 }];
    }

    // PH: strip non-schema fields
    if (enrichedData.Contractdocument.PP.PH) {
      const allowedPH = AUTO_FIELD_ALLOWLIST.PH;
      for (const key of Object.keys(enrichedData.Contractdocument.PP.PH)) {
        if (!allowedPH.has(key)) delete enrichedData.Contractdocument.PP.PH[key];
      }
    }

    // RP: strip non-schema fields (remove RP_VRWRKCD etc.)
    if (enrichedData.Contractdocument.PP.RP) {
      const allowedRP = AUTO_FIELD_ALLOWLIST.RP;
      for (const key of Object.keys(enrichedData.Contractdocument.PP.RP)) {
        if (!allowedRP.has(key)) delete enrichedData.Contractdocument.PP.RP[key];
      }
    }

    // SV: strip non-schema fields (remove SV_VRWRKCD etc.)
    if (enrichedData.Contractdocument.PP.SV) {
      const allowedSV = AUTO_FIELD_ALLOWLIST.SV;
      for (const key of Object.keys(enrichedData.Contractdocument.PP.SV)) {
        if (!allowedSV.has(key)) delete enrichedData.Contractdocument.PP.SV[key];
      }
    }

    // PP_PVADAT must be string per schema
    if (enrichedData.Contractdocument.PP.PP_PVADAT !== undefined && typeof enrichedData.Contractdocument.PP.PP_PVADAT !== 'string') {
      enrichedData.Contractdocument.PP.PP_PVADAT = String(enrichedData.Contractdocument.PP.PP_PVADAT);
    }

    // PP_BMPERC must be integer per schema (BFF may return string)
    if (enrichedData.Contractdocument.PP.PP_BMPERC !== undefined) {
      enrichedData.Contractdocument.PP.PP_BMPERC = parseInt(String(enrichedData.Contractdocument.PP.PP_BMPERC), 10) || 0;
    }

    // Required empty sections per schema
    if (!enrichedData.Contractdocument.PP.TP) enrichedData.Contractdocument.PP.TP = {};
    if (!enrichedData.Contractdocument.PP.VL) enrichedData.Contractdocument.PP.VL = {};
    if (!enrichedData.Contractdocument.PP.CL || !Array.isArray(enrichedData.Contractdocument.PP.CL)) enrichedData.Contractdocument.PP.CL = [];

    // Required sections with defaults per schema
    if (!enrichedData.Contractdocument.PP.QS) enrichedData.Contractdocument.PP.QS = { QA: [] };
    if (!enrichedData.Contractdocument.PP.RI) enrichedData.Contractdocument.PP.RI = { RI_ALGVRWD: 'J' };
    // PH (Pechhulp) - only available for vehicles younger than 12 years
    const phOb = enrichedData.Contractdocument.PP.OB;
    const phBouwjr = parseInt(String(phOb?.OB_BOUWJR || '0'), 10);
    const phBouwmnd = parseInt(String(phOb?.OB_BOUWMND || '1'), 10);
    const phNow = now;
    const phAgeMonths = phBouwjr > 0 ? (phNow.getFullYear() - phBouwjr) * 12 + (phNow.getMonth() + 1 - phBouwmnd) : 0;
    const phEligible = phBouwjr > 0 && phAgeMonths < 12 * 12;
    if (phEligible) {
      if (!enrichedData.Contractdocument.PP.PH) enrichedData.Contractdocument.PP.PH = {};
      enrichedData.Contractdocument.PP.PH.PH_CODE = '2049';
      if (!enrichedData.Contractdocument.PP.PH.PH_GEBIED) enrichedData.Contractdocument.PP.PH.PH_GEBIED = 'X';
      if (!enrichedData.Contractdocument.PP.PH.PH_PPRC) enrichedData.Contractdocument.PP.PH.PH_PPRC = 20;
      // PH_WPREMBP — wijze premieberekening. The PH-defaults block (above) only seeds this
      // when PH already existed; on a fresh enrichment (when PH was created right here) that
      // pass has already run, so the field is absent and Allianz rejects with a schema error.
      if (!enrichedData.Contractdocument.PP.PH.PH_WPREMBP) enrichedData.Contractdocument.PP.PH.PH_WPREMBP = '4';
    } else {
      delete enrichedData.Contractdocument.PP.PH;
    }
    if (!enrichedData.Contractdocument.PP.LC) enrichedData.Contractdocument.PP.LC = { LC_NUMMER: '' };
    // LI: populate from client info (same source as BS)
    if (!enrichedData.Contractdocument.PP.LI) enrichedData.Contractdocument.PP.LI = {};
    const li = enrichedData.Contractdocument.PP.LI;
    const liClient = relationData?.clientInfo;
    const liVp = enrichedData.Contractdocument.PP.VP || {};
    if (!li.LI_RELSRTC) li.LI_RELSRTC = 'A';
    if (!li.LI_ANAAM && liClient?.lastName) li.LI_ANAAM = liClient.lastName;
    if (!li.LI_VOORL && liClient?.initials) li.LI_VOORL = liClient.initials;
    if (!li.LI_GESLACH) li.LI_GESLACH = liClient?.gender || 'O';
    if (!li.LI_GEBDAT && enrichedData.Contractdocument.PP.BS?.BS_GEBDAT) li.LI_GEBDAT = Number(enrichedData.Contractdocument.PP.BS.BS_GEBDAT);
    // Address fields: always mirror VP on recalculation so edits to the policyholder
    // address (VP_PCODE / VP_STRAAT / VP_HUISNR / VP_PLAATS) propagate to LI instead of
    // getting masked by stale values preserved from the previous calculation.
    if (liVp.VP_HUISNR) li.LI_HUISNR = parseInt(String(liVp.VP_HUISNR), 10) || 0;
    if (liVp.VP_PCODE) li.LI_PCODE = liVp.VP_PCODE;
    if (liVp.VP_PLAATS) li.LI_PLAATS = liVp.VP_PLAATS;
    if (liVp.VP_STRAAT) li.LI_STRAAT = liVp.VP_STRAAT;
    if (!enrichedData.Contractdocument.PP.LP) enrichedData.Contractdocument.PP.LP = { LP_ANAAM: '' };

    // CA: ensure array when present
    if (enrichedData.Contractdocument.PP.CA && !Array.isArray(enrichedData.Contractdocument.PP.CA)) {
      enrichedData.Contractdocument.PP.CA = [];
    }

    // Ensure required sections exist (schema requires them even when coverage is not selected)
    if (!enrichedData.Contractdocument.PP.CG) enrichedData.Contractdocument.PP.CG = [];
    {
      const rp = enrichedData.Contractdocument.PP.RP;
      if (!rp || typeof rp !== 'object' || Array.isArray(rp)) {
        enrichedData.Contractdocument.PP.RP = { RP_CODE: '9110', RP_PPRC: 20 };
      }
    }
    if (!enrichedData.Contractdocument.PP.SV) enrichedData.Contractdocument.PP.SV = { SV_CODE: '1002', SV_PPRC: 20 };

    // PP whitelist: strip fields not in schema (additionalProperties: false)
    const allowedPP = AUTO_FIELD_ALLOWLIST.PP;
    for (const key of Object.keys(enrichedData.Contractdocument.PP)) {
      if (!allowedPP.has(key)) delete enrichedData.Contractdocument.PP[key];
    }

    // Strip non-schema fields
    delete enrichedData.Supportsection;
    delete enrichedData.completed;
    delete enrichedData._completed;
    delete enrichedData._timestamp;
    delete enrichedData._uiState;
    delete enrichedData.Contractdocument?.XG;

    // The back-office orders coverages by XX_VOLGNUM (WA first, then CA, then the extras), not by
    // key order. Assign after all sections exist so the running sequence covers every coverage.
    orderAutoCoverages(enrichedData.Contractdocument.PP);

    return {
      carrierCode: AUTO_PRODUCT.carrierCode,
      productName: AUTO_PRODUCT.productName,
      // AFD convention: keys alphabetically sorted on every outgoing document (same as
      // ongevallen). Array order is kept — coverage ordering stays on XX_VOLGNUM.
      input: sortAfdDocument(enrichedData),
    };
}

/**
 * Sort the personenauto contract(s) inside a Relatiedocument alphabetically. The offer
 * chain replaces its working document with each BFF response (unsorted), so this runs
 * right before every outgoing call — mirrors sortOngevallenContracts. A personenauto
 * contract is recognised by its OB (vehicle) section; other products are left untouched.
 */
export const sortPersonenautoContracts = (relatiedocument: any): any => {
  if (Array.isArray(relatiedocument?.Contract)) {
    relatiedocument.Contract = relatiedocument.Contract.map((c: any) =>
      c?.PP?.OB ? sortAfdDocument(c) : c,
    );
  }
  return relatiedocument;
};

export function mergeAutoResult(bffOutput: any, prevData: any) {

    const mergedData = {
      ...prevData,
      ...JSON.parse(JSON.stringify(bffOutput)),
    };

    // Preserve user-entered address data (BA fields)
    if (prevData?.Contractdocument?.PP?.BA) {
      if (!mergedData.Contractdocument) mergedData.Contractdocument = {};
      if (!mergedData.Contractdocument.PP) mergedData.Contractdocument.PP = {};
      mergedData.Contractdocument.PP.BA = { ...prevData.Contractdocument.PP.BA };
    }

    // Preserve CA items from prevData if BFF returned empty CA (BFF may strip them) —
    // but not when the customer affirmatively selected WA-only: there the empty CA is the
    // truth, and restoring stale casco rows would put CA_ data back on every downstream
    // document. When no Hoofddekking is recorded at all (the default-WA user never writes
    // one), only a prev CA row with a real CA_CODE counts as evidence that a casco variant
    // was active: legacy sessions carry flags-only ghost rows ({CA_ERB: 0, CA_NWEGAR,
    // CA_AKPGAR}) that the old applyAutoGarantieFlags invented on WA-only documents, and
    // those must NOT be resurrected.
    const prevCA = prevData?.Contractdocument?.PP?.CA;
    const mergedCA = mergedData.Contractdocument?.PP?.CA;
    const prevDekkingen = prevData?.Supportsection?.Dekkingen;
    const cascoActive = prevDekkingen?.Hoofddekking
      ? hasCascoSelected(prevDekkingen)
      : Array.isArray(prevCA) && prevCA.some((ca: any) => ca?.CA_CODE);
    if (cascoActive
      && Array.isArray(prevCA) && prevCA.length > 0 && Array.isArray(mergedCA) && mergedCA.length === 0) {
      mergedData.Contractdocument.PP.CA = JSON.parse(JSON.stringify(prevCA));
    }

    // Preserve user-selected CA_ERB (eigen risico) - BFF response doesn't echo back the user's choice
    if (prevData?.Contractdocument?.PP?.CA?.[0]?.CA_ERB !== undefined && Array.isArray(mergedData.Contractdocument?.PP?.CA) && mergedData.Contractdocument.PP.CA.length > 0) {
      mergedData.Contractdocument.PP.CA[0].CA_ERB = prevData.Contractdocument.PP.CA[0].CA_ERB;
    }

    // CA_BTWMVZ from OB_BTWCD + garantie flags — single source (autoGarantie.ts).
    applyCaDerivedFields(mergedData.Contractdocument?.PP);

    // Preserve PP-level fields not returned by BFF but required by schema
    const preservePPFields = ['PP_BMINSCH', 'PP_BMNAAND', 'PP_BRANCHE', 'PP_HANDACC', 'PP_IBAN', 'PP_INCWIJZ', 'PP_IVBLETC', 'PP_PPRC', 'PP_SPECLIM', 'PP_SPLIMNR'];
    for (const field of preservePPFields) {
      if (mergedData.Contractdocument?.PP?.[field] === undefined && prevData?.Contractdocument?.PP?.[field] !== undefined) {
        mergedData.Contractdocument.PP[field] = prevData.Contractdocument.PP[field];
      }
    }

    // Preserve required sections not returned by BFF
    const preserveSections = ['BS', 'LC', 'LI', 'LP', 'PH', 'QS', 'RI'];
    for (const section of preserveSections) {
      if (!mergedData.Contractdocument?.PP?.[section] && prevData?.Contractdocument?.PP?.[section]) {
        mergedData.Contractdocument.PP[section] = prevData.Contractdocument.PP[section];
      }
    }

    // Fix types: PP_BMPERC must be integer (BFF may return string like "65.00")
    if (mergedData.Contractdocument?.PP?.PP_BMPERC !== undefined) {
      mergedData.Contractdocument.PP.PP_BMPERC = parseInt(String(mergedData.Contractdocument.PP.PP_BMPERC), 10) || 0;
    }

    // Fix types: OB_BOUWMND must be integer
    if (mergedData.Contractdocument?.PP?.OB?.OB_BOUWMND !== undefined) {
      mergedData.Contractdocument.PP.OB.OB_BOUWMND = parseInt(String(mergedData.Contractdocument.PP.OB.OB_BOUWMND), 10) || 0;
    }

    // Ensure WA_BMTABCD survives BFF merge (BFF may not return it)
    if (mergedData.Contractdocument?.PP?.WA && !mergedData.Contractdocument.PP.WA.WA_BMTABCD) {
      mergedData.Contractdocument.PP.WA.WA_BMTABCD = AUTO_CODELIST.WA_BMTABCD;
    }

    // Strip additional properties from RP and SV (BFF adds *_VRWRKCD which schema disallows).
    // BFF can echo RP back in array form (e.g. `[{RP_CODE, RP_BTP, ...}]` or a stray `[null]`);
    // schema and the rest of the screen expect an object, so unwrap the first valid object — this
    // preserves the BFF-computed RP_BTP that drives the per-coverage price badge — and fall back
    // to defaults only when there's no usable item.
    if (mergedData.Contractdocument?.PP) {
      let rp = mergedData.Contractdocument.PP.RP;
      if (Array.isArray(rp)) {
        rp = rp.find((item: any) => item && typeof item === 'object' && !Array.isArray(item));
        mergedData.Contractdocument.PP.RP = rp;
      }
      if (rp === null || (rp !== undefined && typeof rp !== 'object')) {
        mergedData.Contractdocument.PP.RP = { RP_CODE: '9110', RP_PPRC: 20 };
      } else if (rp) {
        delete rp.RP_VRWRKCD;
      }
    }
    if (mergedData.Contractdocument?.PP?.SV) {
      delete mergedData.Contractdocument.PP.SV.SV_VRWRKCD;
    }

    // Ensure LI is populated with correct types (may be empty from initial state)
    if (mergedData.Contractdocument?.PP) {
      if (!mergedData.Contractdocument.PP.LI) mergedData.Contractdocument.PP.LI = {};
      const mLi = mergedData.Contractdocument.PP.LI;
      const mBs = mergedData.Contractdocument.PP.BS || {};
      const mVp = mergedData.Contractdocument.PP.VP || {};
      if (!mLi.LI_GEBDAT && mBs.BS_GEBDAT) mLi.LI_GEBDAT = Number(mBs.BS_GEBDAT);
      if (!mLi.LI_ANAAM && mBs.BS_ANAAM) mLi.LI_ANAAM = mBs.BS_ANAAM;
      if (!mLi.LI_VOORL && mBs.BS_VOORL) mLi.LI_VOORL = mBs.BS_VOORL;
      if (!mLi.LI_GESLACH && mBs.BS_GESLACH) mLi.LI_GESLACH = mBs.BS_GESLACH;
      if (!mLi.LI_RELSRTC) mLi.LI_RELSRTC = 'A';
      // Address fields: always mirror VP on merge so the UI reflects the latest VP edits
      // instead of whatever was cached in the previous calculation's LI.
      if (mVp.VP_HUISNR) mLi.LI_HUISNR = parseInt(String(mVp.VP_HUISNR), 10) || 0;
      if (mVp.VP_PCODE) mLi.LI_PCODE = mVp.VP_PCODE;
      if (mVp.VP_PLAATS) mLi.LI_PLAATS = mVp.VP_PLAATS;
      if (mVp.VP_STRAAT) mLi.LI_STRAAT = mVp.VP_STRAAT;
      // Enforce types
      if (mLi.LI_GEBDAT !== undefined) mLi.LI_GEBDAT = Number(mLi.LI_GEBDAT);
      if (mLi.LI_HUISNR !== undefined) mLi.LI_HUISNR = parseInt(String(mLi.LI_HUISNR), 10) || 0;
    }

    // AL: preserve AL_APPLNM/AL_URLNAAM from prevData (lost in shallow merge),
    // strip non-schema fields added by BFF, fix BY_BYLSRT type
    if (!mergedData.Contractdocument) mergedData.Contractdocument = {};
    if (!mergedData.Contractdocument.AL) mergedData.Contractdocument.AL = {};
    if (prevData?.Contractdocument?.AL?.AL_APPLNM && !mergedData.Contractdocument.AL.AL_APPLNM) {
      mergedData.Contractdocument.AL.AL_APPLNM = prevData.Contractdocument.AL.AL_APPLNM;
    }
    if (prevData?.Contractdocument?.AL?.AL_URLNAAM && !mergedData.Contractdocument.AL.AL_URLNAAM) {
      mergedData.Contractdocument.AL.AL_URLNAAM = prevData.Contractdocument.AL.AL_URLNAAM;
    }
    delete mergedData.Contractdocument.AL.AL_VRWRKCD;
    delete mergedData.Contractdocument.AL.AL_CPNAAM;
    if (Array.isArray(mergedData.Contractdocument.AL.BY)) {
      for (const by of mergedData.Contractdocument.AL.BY) {
        if (by.BY_BYLSRT !== undefined && typeof by.BY_BYLSRT !== 'string') {
          by.BY_BYLSRT = String(by.BY_BYLSRT);
        }
      }
    }

    // Preserve user-entered OB fields (BFF response may not include all input fields)
    if (prevData?.Contractdocument?.PP?.OB) {
      mergedData.Contractdocument.PP.OB = {
        ...prevData.Contractdocument.PP.OB,
        ...mergedData.Contractdocument?.PP?.OB,
      };
    }

    const mergedOb = mergedData.Contractdocument?.PP?.OB;
    if (mergedOb?.OB_AFMKM !== undefined) {
      mergedOb.OB_AFMKM =
        afmkmCode(mergedOb.OB_AFMKMCD ?? mergedOb.OB_AFMKM) ?? String(mergedOb.OB_AFMKM).padStart(5, '0');
      delete mergedOb.OB_AFMKMCD;
    }

    // Preserve Supportsection selections
    if (prevData?.Supportsection) {
      mergedData.Supportsection = { ...prevData.Supportsection };
    }

    // Compute PP_TTOT from coverage sections (WA + CA + selected optionals)
    const dekkingen = mergedData.Supportsection?.Dekkingen || {};
    const wa = mergedData.Contractdocument?.PP?.WA;
    const ca = mergedData.Contractdocument?.PP?.CA?.[0];
    let total = Number(wa?.WA_TTOT || 0) + Number(ca?.CA_TTOT || 0);

    if (dekkingen.Rechtsbijstand === 'J') total += Number(mergedData.Contractdocument?.PP?.RP?.RP_TTOT || 0);
    if (dekkingen.Schade_Inzittenden === 'J') total += Number(mergedData.Contractdocument?.PP?.SV?.SV_TTOT || 0);
    if (dekkingen.Vrije_keuze_herstelbedrijf === 'J') {
      const wcData = Array.isArray(mergedData.Contractdocument?.PP?.CG) ? mergedData.Contractdocument.PP.CG[0] : undefined;
      total += Number(wcData?.CG_TTOT || 0);
    }
    if (dekkingen.Pechhulp === 'J') total += Number(mergedData.Contractdocument?.PP?.PH?.PH_TTOT || 0);
    mergedData.Contractdocument.PP.PP_TTOT = total;

    return mergedData;
}


// === Module-level calc helpers (moved from PersonenAutoScreen) ===

// BFF /productservice/calculate occasionally 400s on the very first request (cold endpoint).
// Retry once after a short delay so a transient failure doesn't surface as a calculation error.
const CALCULATE_ENDPOINT = '/api/v1/productservice/calculate';
const CALCULATE_RETRY_DELAY_MS = 1500;
export const calculateWithRetry = async (payload: any): Promise<any> => {
  try {
    return await bffClient.request<any>('POST', CALCULATE_ENDPOINT, payload);
  } catch (error: any) {
    console.warn('[PersonenAuto] calculate failed, retrying once after 1500ms:', error?.message || error);
    await new Promise((resolve) => setTimeout(resolve, CALCULATE_RETRY_DELAY_MS));
    return bffClient.request<any>('POST', CALCULATE_ENDPOINT, payload);
  }
};

// Stable hash over the inputs that should trigger a recalculation. Extracted so handleCalculateAll
// can stamp lastCalcHashRef with the post-merge hash and prevent the auto-calc effect from firing
// a second time when mergeResultIntoData normalizes types (e.g. OB_BOUWMND string→number).
export const computeCalcInputHash = (d: any): string => {
  const ob = d?.Contractdocument?.PP?.OB;
  const ba = d?.Contractdocument?.PP?.BA;
  const bs = d?.Contractdocument?.PP?.BS;
  const li = d?.Contractdocument?.PP?.LI;
  return JSON.stringify({
    street: ba?.BA_STRAAT,
    kenteken: ob?.OB_KENTEKE,
    merk: ob?.OB_MERK,
    model: ob?.OB_MODEL,
    bouwjr: ob?.OB_BOUWJR,
    bransto: ob?.OB_BRANSTO,
    aantkw: ob?.OB_AANTKW,
    afmkm: ob?.OB_AFMKM,
    vwaca: ob?.OB_VWACA,
    bouwmnd: ob?.OB_BOUWMND,
    turbo: ob?.OB_TURBO,
    trsoort: ob?.OB_TRSOORT,
    impobj: ob?.OB_IMPOBJ,
    tnoklas: ob?.OB_TNOKLAS,
    schdvry: d?.Contractdocument?.PP?.PP_SCHDVRY,
    hoofdbestuurder: d?.Supportsection?.Divers?.Hoofdbestuurder,
    bsRelsrtc: bs?.BS_RELSRTC,
    bsGebdat: bs?.BS_GEBDAT,
    bsGeslach: bs?.BS_GESLACH,
    bsVoorl: bs?.BS_VOORL,
    bsAnaam: bs?.BS_ANAAM,
    kentekenbewijshouderType: d?.Supportsection?.Divers?.KentekenbewijshouderType,
    liRelsrtc: li?.LI_RELSRTC,
    liGebdat: li?.LI_GEBDAT,
    liGeslach: li?.LI_GESLACH,
    liVoorl: li?.LI_VOORL,
    liAnaam: li?.LI_ANAAM,
    svVerzsom: (() => {
      const sv = d?.Contractdocument?.PP?.SV;
      const svObj = Array.isArray(sv) ? sv[0] : sv;
      return svObj?.SV_VERZSOM;
    })(),
    // OB_EERSTEI gates which of NW (2040) vs AKW (2041) gets pushed into CG, so changing it
    // genuinely shifts the payload and warrants a recalc. OB_AANKDAT gates whether CG 2041
    // is pushed at all — Allianz declines AKW (UWM14) without it. The VKH / NW / AKW toggles
    // do NOT — their CG entries ride on vehicle eligibility (not the toggle), so the BFF
    // would return the same intermediate response on every flip. The toggles only drive the
    // in-page total badge + the final handleNext strip; both happen client-side.
    eersteEigenaar: ob?.OB_EERSTEI,
    aankdat: ob?.OB_AANKDAT,
  });
};
